import { Link } from '@tanstack/react-router'
import { Video } from 'lucide-react'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTimeVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Delivery vehicle panel for the order detail screen: shows the trip's vehicle
 * with a deep link to its camera/GPS view on /tracking, plus the
 * proof-of-delivery snapshot captured from the vehicle camera (when one
 * exists). Renders nothing for orders without a delivery trip or POD photo.
 */
export function OrderDeliveryCameraCard({ order }: { order: SalesOrder }) {
    const vehicle = order.delivery_vehicle
    const podUrl = order.pod_photo_url

    if (!vehicle && !podUrl) return null

    return (
        <Card>
            <CardHeader>
                <CardTitle>{m.order_delivery_vehicle_title()}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {vehicle && (
                    <div className="flex items-center justify-between gap-2">
                        <div>
                            <div className="font-medium">
                                {vehicle.plate_number}
                            </div>
                            <div className="text-muted-foreground">
                                {vehicle.name}
                            </div>
                        </div>
                        <Button asChild size="sm" variant="outline">
                            <Link
                                to="/tracking"
                                search={{ vehicle: vehicle.id }}
                            >
                                <Video className="mr-1 size-4" />
                                {m.order_view_camera_gps()}
                            </Link>
                        </Button>
                    </div>
                )}
                {podUrl && (
                    <div className="space-y-1">
                        <div className="text-muted-foreground">
                            {m.order_pod_photo_title()}
                        </div>
                        <a href={podUrl} target="_blank" rel="noreferrer">
                            <img
                                src={podUrl}
                                alt={m.order_pod_photo_title()}
                                className="max-h-48 w-full rounded-md border object-cover"
                            />
                        </a>
                        {order.pod_photo_captured_at && (
                            <div className="text-xs text-muted-foreground">
                                {formatDateTimeVN(order.pod_photo_captured_at)}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
